import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * useConnectivityStatus — lightweight browser-online awareness for field
 * technicians. Purely observational; does NOT queue writes, does NOT try
 * to sync anything, does NOT touch Firestore configuration.
 *
 * Emits three transient states:
 *   'online'       — steady state; the UI should show nothing.
 *   'offline'      — `navigator.onLine === false`. Persistent until restored.
 *   'reconnecting' — the ~4 seconds immediately after regaining connectivity.
 *                     Consumers can flash a green "Back online" pill and then
 *                     let it collapse back to 'online'.
 *
 * `lastChangeAt` is a millisecond epoch stamped whenever `status` transitions,
 * useful for animating the banner in/out.
 *
 * Consumers can subscribe to `isOffline` directly to gate destructive actions
 * (e.g., "Complete" in the completion dialog).
 */
const RECONNECT_FLASH_MS = 4000;

export function useConnectivityStatus() {
  const initial = typeof navigator !== 'undefined' && navigator.onLine === false
    ? 'offline'
    : 'online';
  const [status, setStatus] = useState(initial);
  const [lastChangeAt, setLastChangeAt] = useState(Date.now());
  const reconnectTimerRef = useRef(null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOffline = () => {
      clearReconnectTimer();
      setStatus('offline');
      setLastChangeAt(Date.now());
    };

    const handleOnline = () => {
      setStatus('reconnecting');
      setLastChangeAt(Date.now());
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        setStatus('online');
        setLastChangeAt(Date.now());
        reconnectTimerRef.current = null;
      }, RECONNECT_FLASH_MS);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearReconnectTimer();
    };
  }, [clearReconnectTimer]);

  return {
    status,
    isOffline: status === 'offline',
    isReconnecting: status === 'reconnecting',
    lastChangeAt,
  };
}

export default useConnectivityStatus;
